import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchSiteGridboxCoverage, type GridboxCoverageDay } from '@/api/sites';

type Props = {
  open: boolean;
  onClose: () => void;
  siteUuid: string;
  siteName: string;
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function monthLabel(year: number, month: number): string {
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  return `${months[month - 1]} ${year}`;
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const totalMonths = year * 12 + (month - 1) + delta;
  return { year: Math.floor(totalMonths / 12), month: (totalMonths % 12) + 1 };
}

// Pazartesi-başlangıçlı 0..6 ofset
function dayOfWeekMon(year: number, month: number, day: number): number {
  const d = new Date(Date.UTC(year, month - 1, day));
  return (d.getUTCDay() + 6) % 7;
}

type Status = 'none' | 'raw_only' | 'full' | 'expected_missing';

function lpStatus(day: GridboxCoverageDay): Status {
  if (day.load_profile.data > 0) return 'full';
  if (day.load_profile.raw > 0) return 'raw_only';
  return 'none';
}

function endexStatus(day: GridboxCoverageDay): Status {
  if (day.endex.data > 0) return 'full';
  if (day.endex.raw > 0) return 'raw_only';
  return 'none';
}

function billingStatus(day: GridboxCoverageDay, isFirstOfMonth: boolean): Status {
  if (day.endex_billing.data > 0) return 'full';
  if (day.endex_billing.raw > 0) return 'raw_only';
  if (isFirstOfMonth) return 'expected_missing';
  return 'none';
}

const COLOR: Record<Status, string> = {
  none:              'bg-slate-200',
  raw_only:          'bg-amber-400',
  full:              'bg-emerald-500',
  expected_missing:  'bg-rose-400',
};

const STATUS_LABELS: Record<Status, string> = {
  none: 'veri yok',
  raw_only: 'sadece raw',
  full: 'raw + data',
  expected_missing: 'beklenen ama eksik',
};

function StatusDot({ status, title }: { status: Status; title: string }) {
  return (
    <div
      className={`h-2.5 w-2.5 rounded-full ${COLOR[status]}`}
      title={`${title}: ${STATUS_LABELS[status]}`}
    />
  );
}

export default function GridboxCoverageModal({ open, onClose, siteUuid, siteName }: Props) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState<GridboxCoverageDay[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!siteUuid) return;
    setLoading(true);
    try {
      const res = await fetchSiteGridboxCoverage(siteUuid, year, month);
      setDays(res.days);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as Error).message;
      toast.error(`Takvim yüklenemedi: ${message ?? 'bilinmeyen hata'}`);
      setDays([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteUuid, year, month]);

  const dayMap = useMemo(() => {
    const m = new Map<string, GridboxCoverageDay>();
    for (const d of days) m.set(d.date, d);
    return m;
  }, [days]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOffset = dayOfWeekMon(year, month, 1);
  const cells: Array<{ key: string; day?: number; date?: string }> = [];
  for (let i = 0; i < firstDayOffset; i++) cells.push({ key: `pad-${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${pad2(month)}-${pad2(d)}`;
    cells.push({ key: dateStr, day: d, date: dateStr });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">GridBox Veri Takvimi</h3>
            <p className="text-xs text-slate-500">{siteName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
            title="Kapat"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { const n = shiftMonth(year, month, -1); setYear(n.year); setMonth(n.month); }}
              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
              title="Önceki ay"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="min-w-[160px] px-3 text-center text-sm font-bold text-slate-800">
              {monthLabel(year, month)}
            </div>
            <button
              type="button"
              onClick={() => { const n = shiftMonth(year, month, 1); setYear(n.year); setMonth(n.month); }}
              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
              title="Sonraki ay"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              onClick={() => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth() + 1); }}
              className="ml-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Bu Ay
            </button>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>

        <div className="px-6 pb-2">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map((d) => (
              <div key={d} className="py-1">{d}</div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell) => {
              if (!cell.day || !cell.date) {
                return <div key={cell.key} />;
              }
              const day = dayMap.get(cell.date);
              const isFirst = cell.day === 1;
              const lp = day ? lpStatus(day) : 'none';
              const en = day ? endexStatus(day) : 'none';
              const eb = day ? billingStatus(day, isFirst) : (isFirst ? 'expected_missing' : 'none');

              return (
                <div
                  key={cell.key}
                  className="rounded-lg border border-slate-100 bg-white p-1.5 text-center"
                  title={day ? `LP raw=${day.load_profile.raw} data=${day.load_profile.data}\nEndex raw=${day.endex.raw} data=${day.endex.data}\nFatura raw=${day.endex_billing.raw} data=${day.endex_billing.data}` : ''}
                >
                  <div className="mb-1 text-xs font-bold text-slate-700">{cell.day}</div>
                  <div className="flex items-center justify-center gap-1">
                    <StatusDot status={lp} title="Load Profile" />
                    <StatusDot status={en} title="Endeks" />
                    {isFirst && <StatusDot status={eb} title="Fatura Endeks" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-700">Sıra:</span>
              <span>Load Profile</span>
              <span className="text-slate-300">•</span>
              <span>Endeks</span>
              <span className="text-slate-300">•</span>
              <span>Fatura Endeks (sadece ayın 1'i)</span>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-slate-200" /> yok</div>
              <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> sadece raw</div>
              <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> raw + data</div>
              <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-rose-400" /> eksik (beklenen)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
